import type { OperatorId } from "../core/RunState.ts";
import type { CombatSystem } from "./CombatSystem.ts";
import type { EconomySystem } from "./EconomySystem.ts";
import type { SynergySystem } from "./SynergySystem.ts";
import type { RuntimeState } from "../game/types.ts";
import { gainLives, setLives } from "../game/stateIntents.ts";
import { ELEMENTS } from "../entities/elements.ts";

/**
 * Context required to apply operator effects to game systems.
 */
export interface OperatorContext {
  gs: RuntimeState;
  combatSystem: CombatSystem;
  economySystem: EconomySystem;
  synergySystem: SynergySystem;
}

export interface AppliedOperatorEffect {
  operator: OperatorId;
  description: string;
}

/**
 * D-322 / AC-5 (Task-007) — the operator numeric magnitudes, lifted out of the
 * `apply()` closures below so they can be overridden from `operators.json`
 * (data read first, this object = the byte-safe code fallback). Mirrors the
 * `FIREWALL_DEFAULTS` / `applyFirewallData` mutable-singleton seam
 * (`entities/Firewall.ts`) — every `apply()` reads this live object, so
 * {@link applyOperatorsData} needs no call-site changes. The effect
 * STRUCTURE (which system each operator touches, in what order) is
 * unchanged — only these magnitudes are data-driven.
 */
export interface OperatorMagnitudes {
  "sentinel-7": { lives: number; rangeMult: number };
  warden: { startingCredits: number; livesDelta: number };
  ghost: { freqMult: number; elementBonus: number };
  architect: { sellMultiplier: number; startingCredits: number };
  cipher: { rangeMult: number; freqMult: number };
}

export const OPERATOR_MAGNITUDES: OperatorMagnitudes = {
  "sentinel-7": { lives: 1, rangeMult: 1.05 },
  warden: { startingCredits: 50, livesDelta: -1 },
  ghost: { freqMult: 1.15, elementBonus: 0.9 },
  architect: { sellMultiplier: 0.85, startingCredits: 25 },
  cipher: { rangeMult: 1.08, freqMult: 1.1 },
};

/**
 * Operator unlock thresholds — total completed runs required to unlock each
 * operator. Byte-safe code fallback; overridden in place by
 * {@link applyOperatorsData}.
 */
export const OPERATOR_UNLOCK_THRESHOLDS: Record<string, number> = {
  "sentinel-7": 0,
  warden: 3,
  ghost: 5,
  architect: 8,
  cipher: 12,
};

/** Raw shape of `public/data/operators.json` (pre-validation). */
export interface OperatorsDataFile {
  magnitudes?: Partial<
    Record<keyof OperatorMagnitudes, Partial<Record<string, number>>>
  >;
  unlockThresholds?: Partial<Record<string, number>>;
}

/**
 * D-322 / AC-5 (Task-007) — apply the optional `operators.json` override onto
 * {@link OPERATOR_MAGNITUDES} / {@link OPERATOR_UNLOCK_THRESHOLDS} in place.
 * Absent/unset fields keep the byte-identical code default.
 */
export function applyOperatorsData(data?: OperatorsDataFile | null): void {
  if (!data) return;
  if (data.magnitudes) {
    for (const [op, mags] of Object.entries(data.magnitudes)) {
      const target = (
        OPERATOR_MAGNITUDES as unknown as Record<string, Record<string, number>>
      )[op];
      if (target && mags) Object.assign(target, mags);
    }
  }
  if (data.unlockThresholds) {
    Object.assign(OPERATOR_UNLOCK_THRESHOLDS, data.unlockThresholds);
  }
}

/**
 * Operator stat profiles.
 * Applied ONCE at run start, BEFORE module effects.
 */
const OPERATOR_EFFECTS: Record<
  string,
  { apply: (ctx: OperatorContext) => string }
> = {
  "sentinel-7": {
    apply(ctx) {
      const m = OPERATOR_MAGNITUDES["sentinel-7"];
      gainLives(ctx.gs, m.lives);
      ctx.combatSystem.applyRangeMultiplier(m.rangeMult);
      return "+1 HP, +5% tower range";
    },
  },
  warden: {
    apply(ctx) {
      const m = OPERATOR_MAGNITUDES.warden;
      ctx.economySystem.grantStartingCredits(m.startingCredits, "operator:warden");
      setLives(ctx.gs, Math.max(1, ctx.gs.lives + m.livesDelta));
      return "+50 starting gold, -1 HP";
    },
  },
  ghost: {
    apply(ctx) {
      const m = OPERATOR_MAGNITUDES.ghost;
      ctx.synergySystem.frequencyMultiplier =
        ctx.synergySystem.frequencyMult * m.freqMult;
      // GHOST's promise is "-10% DMG" (`operator.ghost.desc`, both locales) —
      // ALL tower damage, not a subset. It used to name three elements by hand
      // (fire / water / lightning) while `towers.json` ships SIX families; the
      // other three (signal, kinetic, virus) took no penalty at all, because
      // `DamageCalculation` only multiplies when `elementBonuses[element]`
      // exists. A kinetic build got GHOST's +15% synergy frequency with no
      // downside whatsoever — found by the 2026-08-16 gameplay-effect audit.
      //
      // Fixed at the domain, not in the copy: the penalty now walks ELEMENTS,
      // the canonical single source of truth (`entities/elements.ts`), so a
      // seventh family added there is covered the day it is added instead of
      // silently escaping. `applyElementBonus` composes multiplicatively, so
      // this still stacks correctly with anything else touching an element.
      for (const element of ELEMENTS) {
        ctx.combatSystem.applyElementBonus(element, m.elementBonus);
      }
      return "Synergy frequency +15%, -10% tower damage";
    },
  },
  architect: {
    apply(ctx) {
      const m = OPERATOR_MAGNITUDES.architect;
      ctx.economySystem.sellMultiplier = m.sellMultiplier;
      ctx.economySystem.grantStartingCredits(
        m.startingCredits,
        "operator:architect",
      );
      return "+15% sell value, +25 starting gold";
    },
  },
  cipher: {
    apply(ctx) {
      const m = OPERATOR_MAGNITUDES.cipher;
      ctx.combatSystem.applyRangeMultiplier(m.rangeMult);
      ctx.synergySystem.frequencyMultiplier =
        ctx.synergySystem.frequencyMult * m.freqMult;
      return "+8% tower range, +10% synergy frequency";
    },
  },
};

/**
 * Apply operator profile effects at run start.
 * Called BEFORE applyModulesToSystems (base stats → then augments).
 */
export function applyOperatorToSystems(
  operator: OperatorId,
  ctx: OperatorContext,
): AppliedOperatorEffect | null {
  const effect = OPERATOR_EFFECTS[operator];
  if (!effect) return null;

  const description = effect.apply(ctx);
  return { operator, description };
}

/**
 * Unlock thresholds — total completed runs required to unlock each operator.
 * Tuned to escalate at a learnable pace: every 3-4 runs you earn something new.
 *  - sentinel-7: default (always available)
 *  - warden:     3 runs (early-game variety)
 *  - ghost:      5 runs (mid-game variety)
 *  - architect:  8 runs (rewards persistence past first ending)
 *  - cipher:    12 runs (long-tail collection goal)
 */
export function getOperatorUnlockThreshold(op: OperatorId): number {
  return OPERATOR_UNLOCK_THRESHOLDS[op] ?? Infinity;
}
